import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Router, UNSAFE_createBrowserHistory as createBrowserHistory } from "react-router-dom";
import { getSlug, setSlug, subscribeSlug, detectSlug, toInternal, toExternal, mapTo } from "../lib/portalSlug";

const history = createBrowserHistory({ v5Compat: true });
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* BrowserRouter that shows a client's portal under its branded prefix (/apc/…) while the app routes on /portal/… */
export default function SlugRouter({ children }) {
  const [slug, setSlugState] = useState(getSlug());
  const [state, setState] = useState({ action: history.action, location: history.location });
  // A branded URL (/apc/jobs) opened cold with nothing remembered: confirm the link is real before treating it as a portal prefix
  const [pending, setPending] = useState(() => (getSlug() ? "" : detectSlug(history.location.pathname)));
  useLayoutEffect(() => subscribeSlug(setSlugState), []);
  useLayoutEffect(() => history.listen(setState), []);
  useEffect(() => {
    if (!pending) return;
    fetch(`${BACKEND}/api/public/portal-brand/${pending}`)
      .then((r) => { if (r.ok) setSlug(pending); })
      .catch(() => {})
      .finally(() => setPending(""));
  }, [pending]);

  // Canonicalise the address bar: /portal/x → /apc/x once we know the slug (and /oldslug/x → /apc/x if it changed)
  useLayoutEffect(() => {
    const ext = state.location.pathname;
    const canon = toExternal(toInternal(ext, slug), slug);
    if (canon !== ext) history.replace({ ...state.location, pathname: canon });
  }, [slug, state.location]);

  const navigator = useMemo(() => ({
    createHref: (to) => history.createHref(mapTo(to, slug)),
    encodeLocation: (to) => history.encodeLocation(mapTo(to, slug)),
    go: (n) => history.go(n),
    push: (to, s) => history.push(mapTo(to, slug), s),
    replace: (to, s) => history.replace(mapTo(to, slug), s),
  }), [slug]);

  const location = useMemo(() => ({ ...state.location, pathname: toInternal(state.location.pathname, slug) }), [state.location, slug]);
  if (pending) return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="portal-link-check">
      <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );
  return <Router location={location} navigationType={state.action} navigator={navigator}>{children}</Router>;
}
